import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  IconButton,
} from '@mui/material'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'

interface MetaTraderAccount {
  login: string
  password: string
  server: string
  platform: 'mt4' | 'mt5'
  nickname?: string
}

type ReadOnlyField = 'login' | 'server' | 'platform' | 'nickname'

interface MetaTraderAccountFormProps {
  account: MetaTraderAccount
  onAccountKeyChange: (key: string, value: any) => void
  showPassword: boolean
  setShowPassword: (show: boolean) => void
  readOnlyFields?: ReadOnlyField[]
}

function MetaTraderAccountForm({
  account,
  onAccountKeyChange,
  showPassword,
  setShowPassword,
  readOnlyFields = [],
}: MetaTraderAccountFormProps) {
  const ro = (field: ReadOnlyField) => readOnlyFields.includes(field)

  return (
    <Box className="ph-no-capture" display="flex" flexDirection="column" gap={3}>
      <TextField
        fullWidth
        label="Account Nickname *"
        value={account.nickname || ''}
        onChange={(e) => onAccountKeyChange('nickname', e.target.value)}
        placeholder="e.g., My Demo Account, Live Trading Account"
        variant="outlined"
        helperText="A friendly name to help you identify this account"
        disabled={ro('nickname')}
      />

      <TextField
        fullWidth
        label="Login *"
        value={account.login}
        onChange={(e) => {
          const value = e.target.value
          if (/^\d*$/.test(value)) {
            onAccountKeyChange('login', value)
          }
        }}
        placeholder="Enter your MetaTrader login (digits only)"
        variant="outlined"
        inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
        disabled={ro('login')}
      />

      <TextField
        fullWidth
        label="Password *"
        type={showPassword ? 'text' : 'password'}
        value={account.password}
        onChange={(e) => onAccountKeyChange('password', e.target.value)}
        placeholder="Enter your MetaTrader password"
        variant="outlined"
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <TextField
        fullWidth
        label="Server *"
        value={account.server}
        onChange={(e) => onAccountKeyChange('server', e.target.value)}
        placeholder="e.g., MetaQuotes-Demo, ICMarkets-Live01"
        variant="outlined"
        helperText="The server name from your broker"
        disabled={ro('server')}
      />

      <FormControl fullWidth disabled={ro('platform')}>
        <InputLabel>Platform *</InputLabel>
        <Select
          value={account.platform}
          label="Platform"
          onChange={(e) => onAccountKeyChange('platform', e.target.value)}
        >
          <MenuItem value="mt4">MetaTrader 4</MenuItem>
          <MenuItem value="mt5">MetaTrader 5</MenuItem>
        </Select>
      </FormControl>
    </Box>
  )
}

export default MetaTraderAccountForm
export type { MetaTraderAccount, MetaTraderAccountFormProps, ReadOnlyField }
